import React from "react";
import { Move, Trash2, Star, AlertTriangle, Info } from "react-feather";
import BaseTooltip from "../BaseTooltip";
import "./ComponentRow.css";

const ComponentRow = ({
  component,
  onUpdate,
  onRemove,
  onSetResidual,
  availableBaseComponents = [],
  hasCircularError = false,
  allocationPercent = null,
  index,
  isOnboarding = false,
}) => {
  const {
    id,
    name,
    calculationType,
    fixedAmount,
    percentage,
    baseComponentId,
    isTaxable,
    isProrata,
    isResidual,
  } = component;

  const isFixed = calculationType === "fixed";
  const isPctOfGross =
    calculationType === "percentage_of_gross" || calculationType === "percentage_of_ctc";
  const isPctOfComp =
    calculationType === "percentage" || calculationType === "percentage_of_component";
  const isResidualType = calculationType === "residual" || !!isResidual;

  const getAllocModifier = () => {
    if (allocationPercent == null) return "gray";
    if (allocationPercent > 100) return "danger";
    if (allocationPercent >= 99.9) return "success";
    return "warning";
  };

  return (
    <div className="comp-row-container">
      <div
        className={`comp-row-card ${isResidualType ? "residual" : ""} ${
          hasCircularError ? "circular-error" : ""
        }`}
      >
        {/* ── ROW 1: Main controls ── */}
        <div className="comp-row-inner">
          {/* Drag handle */}
          <span className="comp-drag-handle" aria-label={`Drag to reorder ${index + 1}`}>
            <Move size={14} aria-hidden="true" />
          </span>

          {/* Name — grows to fill available space */}
          <input
            className="comp-field-input comp-name-input"
            type="text"
            aria-label="Component name"
            placeholder="Component name"
            value={name ?? ""}
            onChange={(e) => onUpdate(id, "name", e.target.value)}
          />

          {/* Type toggle group */}
          <div
            className={`comp-type-group ${isOnboarding ? "onboarding" : ""}`}
            role="group"
            aria-label="Calculation type"
          >
            <BaseTooltip text="A static amount that remains constant every month, independent of gross salary levels.">
              <button
                type="button"
                aria-pressed={isFixed}
                className={`comp-type-btn fixed ${isFixed ? "active" : ""} ${
                  isOnboarding ? "onboarding" : ""
                }`}
                onClick={() => onUpdate(id, "calculationType", "fixed")}
              >
                Fixed ₹
              </button>
            </BaseTooltip>
            <BaseTooltip text="Calculated dynamically as a percentage of the employee's monthly target Gross Salary.">
              <button
                type="button"
                aria-pressed={isPctOfGross}
                className={`comp-type-btn pct-gross ${isPctOfGross ? "active" : ""} ${
                  isOnboarding ? "onboarding" : ""
                }`}
                onClick={() => onUpdate(id, "calculationType", "percentage_of_gross")}
              >
                % of Salary
              </button>
            </BaseTooltip>
            <BaseTooltip text="Calculated dynamically as a percentage of another component (e.g. HRA as 40% of Basic Salary).">
              <button
                type="button"
                aria-pressed={isPctOfComp}
                className={`comp-type-btn pct-comp ${isPctOfComp ? "active" : ""} ${
                  isOnboarding ? "onboarding" : ""
                }`}
                onClick={() => onUpdate(id, "calculationType", "percentage_of_component")}
              >
                % of Comp
              </button>
            </BaseTooltip>
            <BaseTooltip text="Automatically absorbs the remaining target Gross Salary after all other component calculations are completed.">
              <button
                type="button"
                aria-pressed={isResidualType}
                className={`comp-type-btn residual ${isResidualType ? "active" : ""} ${
                  isOnboarding ? "onboarding" : ""
                }`}
                onClick={() =>
                  onSetResidual ? onSetResidual(id) : onUpdate(id, "calculationType", "residual")
                }
              >
                Residual
              </button>
            </BaseTooltip>
          </div>

          {/* Fixed amount */}
          {isFixed && (
            <input
              className="comp-field-input comp-fixed-input"
              type="number"
              aria-label="Fixed amount"
              placeholder="Amount ₹"
              value={fixedAmount ?? ""}
              min={0}
              onChange={(e) =>
                onUpdate(id, "fixedAmount", e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          )}

          {/* Percentage */}
          {(isPctOfGross || isPctOfComp) && (
            <input
              className="comp-field-input comp-pct-input"
              type="number"
              aria-label="Percentage"
              placeholder="%"
              value={percentage ?? ""}
              min={0}
              max={100}
              onChange={(e) =>
                onUpdate(id, "percentage", e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          )}

          {/* Base component — % of comp */}
          {isPctOfComp && (
            <div className="comp-select-base">
              <span className="comp-select-base-label">of</span>
              <select
                aria-label="Base component"
                className="comp-field-input comp-base-select"
                value={
                  availableBaseComponents.find(
                    (bc) =>
                      String(bc.id) === String(baseComponentId) || bc.name === baseComponentId,
                  )?.id ??
                  baseComponentId ??
                  ""
                }
                onChange={(e) => onUpdate(id, "baseComponentId", e.target.value || null)}
              >
                <option value="">Select component…</option>
                {availableBaseComponents.map((bc) => (
                  <option key={bc.id} value={bc.id}>
                    {bc.name || `Component ${bc.id}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* % of Salary badge */}
          {isPctOfGross && <span className="comp-badge-salary">of monthly salary</span>}

          {/* Residual badge */}
          {isResidualType && (
            <span className="comp-badge-residual">
              <Star size={11} strokeWidth={2.5} /> Auto-balance
            </span>
          )}
        </div>

        {/* ── ROW 2: Metadata ── */}
        <div className="comp-meta-row">
          {/* Allocation % pill */}
          {allocationPercent != null && !isResidualType ? (
            <span className={`comp-alloc-pill ${getAllocModifier()}`}>
              {allocationPercent.toFixed(1)}%
            </span>
          ) : (
            <span className="comp-alloc-spacer" />
          )}

          {/* Prorata */}
          {!isResidualType && (
            <div className="comp-checkbox-wrap">
              <label className="comp-checkbox-label">
                <input
                  type="checkbox"
                  aria-label="Prorate by attendance"
                  className="comp-checkbox-input"
                  checked={isProrata !== false}
                  onChange={(e) => onUpdate(id, "isProrata", e.target.checked)}
                />
                Prorata
              </label>
              <BaseTooltip
                label="Prorata: When enabled, this component is adjusted (pro-rated) based on the number of days the employee actually worked in the month. Turn off for fixed monthly payouts that do not change based on attendance (e.g., fixed conveyance allowance)."
                position="top"
              >
                <span className="comp-info-icon">
                  <Info size={13} strokeWidth={2.5} />
                </span>
              </BaseTooltip>
            </div>
          )}

          {/* Taxable */}
          <div className="comp-checkbox-wrap">
            <label className="comp-checkbox-label">
              <input
                type="checkbox"
                aria-label="Taxable"
                className="comp-checkbox-input"
                checked={!!isTaxable}
                onChange={(e) => onUpdate(id, "isTaxable", e.target.checked)}
              />
              Taxable
            </label>
            <BaseTooltip
              label="Taxable: When enabled, this earning is subject to income tax. It will be added to the employee's taxable gross income for TDS (Tax Deducted at Source) calculation."
              position="top"
            >
              <span className="comp-info-icon">
                <Info size={13} strokeWidth={2.5} />
              </span>
            </BaseTooltip>
          </div>

          {/* Spacer */}
          <div className="comp-meta-spacer" />

          {/* Delete */}
          <button
            type="button"
            aria-label="Remove component"
            onClick={() => onRemove(id)}
            className="comp-delete-btn"
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Circular dependency error */}
      {hasCircularError && (
        <div className="comp-circular-error" role="alert" aria-live="polite">
          <AlertTriangle size={12} strokeWidth={2.5} /> Circular dependency detected
        </div>
      )}
    </div>
  );
};

export default ComponentRow;
